import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import AnimatedNumber from "@/components/AnimatedNumber";

function Milestone({ m, isLatest, index }) {
  return (
    <motion.div
      data-testid={`milestone-${m.id}`}
      initial={{ opacity: 0, x: -28 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className="relative pl-10 md:pl-14 pb-14 last:pb-0"
    >
      {/* timeline dot */}
      <span className="absolute left-0 top-2 grid place-items-center">
        <span className={`h-3.5 w-3.5 rounded-full ${isLatest ? "bg-ptf-accent" : "bg-ptf-bg/40"}`} />
        {isLatest && <span className="absolute h-8 w-8 rounded-full bg-ptf-accent/25 animate-ping" />}
      </span>

      <div className="flex flex-wrap items-center gap-3">
        {isLatest && (
          <span data-testid="latest-milestone-badge" className="inline-flex items-center gap-1.5 bg-ptf-accent text-ptf-primary text-[0.6rem] font-semibold uppercase tracking-[0.16em] px-2.5 py-1">
            <Sparkles className="h-3 w-3" /> Latest milestone
          </span>
        )}
        {m.date && <span className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-bg/60">{m.date}</span>}
        <span className="text-[0.6rem] uppercase tracking-[0.16em] border border-ptf-bg/25 px-2 py-0.5 text-ptf-bg/60">{m.dimension}</span>
      </div>

      <p className={`mt-4 font-display font-extrabold leading-none ${isLatest ? "text-6xl md:text-7xl text-ptf-accent" : "text-5xl md:text-6xl text-ptf-bg"}`}>
        <AnimatedNumber value={m.value} />
      </p>
      <p className="mt-3 text-sm md:text-base uppercase tracking-[0.14em] text-ptf-bg/75">{m.label}</p>
      {m.note && <p className="mt-3 max-w-xl text-sm text-ptf-bg/55 leading-relaxed font-serif-ed italic">{m.note}</p>}
    </motion.div>
  );
}

export default function ImpactTimeline({ metrics = [] }) {
  const ts = (m) => {
    const d = Date.parse(m.date || "");
    if (!Number.isNaN(d)) return d;
    const c = Date.parse(m.created_at || "");
    return Number.isNaN(c) ? 0 : c;
  };
  const sorted = [...metrics].sort((a, b) => ts(b) - ts(a));
  return (
    <section data-testid="impact-timeline" className="bg-ptf-primary text-ptf-bg py-20 md:py-28 grain relative">
      <div className="relative z-10 mx-auto max-w-[1100px] px-5 sm:px-8 lg:px-12">
        <p className="text-[0.7rem] uppercase tracking-[0.28em] text-ptf-accent mb-6 flex items-center gap-3">
          <span className="h-px w-8 bg-ptf-accent inline-block" /> Impact milestones
        </p>
        <h2 className="font-display font-extrabold uppercase text-4xl md:text-5xl tracking-tight leading-[0.95] max-w-2xl">
          Verified, one milestone at a time.
        </h2>
        <p className="mt-5 max-w-xl text-ptf-bg/70 leading-relaxed">
          Every figure below is measured and verified before it is published. Together
          they form a growing record of what our programmes actually achieve.
        </p>

        <div className="relative mt-16">
          <span className="absolute left-[6px] top-2 bottom-2 w-px bg-ptf-bg/20" aria-hidden="true" />
          {sorted.map((m, i) => (
            <Milestone key={m.id} m={m} isLatest={i === 0} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
